import React from 'react';
import { Select, MenuItem, InputLabel, FormControl, TextField, Grid, SelectChangeEvent } from '@mui/material';

/**
 * SHER-464 — optional "Moment de prise" (time of intake) for a prescribed
 * medication. A dropdown of meal-relative options; choosing "Autre" reveals a
 * free-text field. Optional by design (medications like injections/topicals are
 * not meal-related), so an empty selection is valid.
 *
 * Controlled value shape: `{ moment, other }`.
 *  - `moment` = one of MEAL_INSTRUCTION_OPTIONS values, or "" (unset).
 *  - `other`  = free text, only meaningful when `moment === "other"`.
 */
export interface MealInstructionValue {
    moment: string;
    other: string;
}

export const MEAL_INSTRUCTION_OPTIONS = [
    { value: 'fasting', labelKey: 'hospital.meal-options.fasting' },
    { value: 'before_meal', labelKey: 'hospital.meal-options.before_meal' },
    { value: 'during_meal', labelKey: 'hospital.meal-options.during_meal' },
    { value: 'after_meal', labelKey: 'hospital.meal-options.after_meal' },
    { value: 'indifferent', labelKey: 'hospital.meal-options.indifferent' },
    { value: 'other', labelKey: 'hospital.meal-options.other' },
];

export const OTHER_MOMENT = 'other';

interface MealInstructionFieldProps {
    value?: MealInstructionValue | null;
    onChange: (value: MealInstructionValue) => void;
    translate: (key: string) => string;
    label?: string;
    disabled?: boolean;
}

const MealInstructionField: React.FC<MealInstructionFieldProps> = ({
    value,
    onChange,
    translate,
    label,
    disabled,
}) => {
    const current: MealInstructionValue = {
        moment: value?.moment ?? '',
        other: value?.other ?? '',
    };
    const fieldLabel = label ?? translate('hospital.meal-options.label');

    function handleMomentChange(event: SelectChangeEvent<string>) {
        const moment = event.target.value;
        // Drop any stale free-text when leaving the "Autre" option.
        onChange({ moment, other: moment === OTHER_MOMENT ? current.other : '' });
    }

    function handleOtherChange(text: string) {
        onChange({ moment: OTHER_MOMENT, other: text });
    }

    const isOther = current.moment === OTHER_MOMENT;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" margin="dense">
                    <InputLabel id="meal-instruction-label">{fieldLabel}</InputLabel>
                    <Select
                        labelId="meal-instruction-label"
                        id="meal-instruction"
                        label={fieldLabel}
                        disabled={disabled}
                        value={current.moment}
                        onChange={handleMomentChange}
                        displayEmpty
                        data-testid="meal-instruction-select"
                    >
                        {/* Optional field: allow clearing back to "no instruction". */}
                        <MenuItem value="">
                            <em>{translate('hospital.meal-options.none')}</em>
                        </MenuItem>
                        {MEAL_INSTRUCTION_OPTIONS.map((option) => (
                            <MenuItem value={option.value} key={option.value}>
                                {translate(option.labelKey)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            {isOther && (
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        label={translate('hospital.meal-options.other-specify')}
                        value={current.other}
                        onChange={(e) => handleOtherChange(e.target.value)}
                        disabled={disabled}
                        data-testid="meal-instruction-other"
                    />
                </Grid>
            )}
        </Grid>
    );
};

export default MealInstructionField;
